import { useRef, useState } from 'react';
import { toast } from 'react-toastify';

// null hands a finished toast back to the ToastContainer defaults (auto close, close
// button...), the same reset toast.promise applies when it settles.
const SETTLED_TOAST = {
  isLoading: false,
  autoClose: null,
  closeOnClick: null,
  closeButton: null,
  draggable: null
};

const formatBytes = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Count-click report downloads. For a large range the server builds the whole file before
// the first byte arrives, so a click could look like it did nothing. The clicked cell gets
// a spinner (isDownloading) and one toast walks through preparing -> downloading -> done
// or failed. Downloads are keyed by `name`; a second click on the same one is ignored
// while it runs, other cells can still download in parallel.
const useReportDownload = () => {
  const activeRef = useRef(new Set());
  const [activeNames, setActiveNames] = useState([]);

  const setActive = (name, isActive) => {
    if (isActive) activeRef.current.add(name);
    else activeRef.current.delete(name);
    setActiveNames([...activeRef.current]);
  };

  const isDownloading = (name) => activeNames.includes(name);

  // `request` gets axios' onDownloadProgress and must resolve to the file Blob.
  const downloadFile = async ({ name, count, fileName, request }) => {
    if (activeRef.current.has(name)) return;
    setActive(name, true);

    const records = Number(count) || 0;
    const toastId = toast.loading(
      `Preparing ${name} report (${records.toLocaleString()} records)...`
    );

    // Progress fires per received chunk; only re-render the toast when the text changes.
    // `total` is missing when the server does not send Content-Length, so fall back to size.
    let lastProgress = '';
    const onDownloadProgress = ({ loaded, total }) => {
      const progress = total ? `${Math.round((loaded / total) * 100)}%` : formatBytes(loaded);
      if (progress === lastProgress) return;
      lastProgress = progress;
      toast.update(toastId, { render: `Downloading ${name} report... ${progress}` });
    };

    try {
      const blob = await request(onDownloadProgress);
      if (!blob) throw new Error('Empty download response');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.update(toastId, {
        ...SETTLED_TOAST,
        type: 'success',
        render: `${name} report downloaded`
      });
    } catch (error) {
      // Blob error bodies never reach the axios interceptor's toast, so say it here.
      console.log('error', error);
      toast.update(toastId, {
        ...SETTLED_TOAST,
        type: 'error',
        render: `Could not download ${name} report. Please try again.`
      });
    } finally {
      setActive(name, false);
    }
  };

  return { isDownloading, downloadFile };
};

export default useReportDownload;
